import type { ErrorMessage } from '@repo/types/messages/error';
import type { ErrorMessages } from '@repo/types/messages/errors';

import { isAxiosError } from 'axios';

import { baseErrorMessages } from '#messages/baseErrorMessages';
import { defaultErrorMessage } from '#messages/defaultErrorMessage';

export class ErrorMessageHandler {
  private messages: ErrorMessages;
  private defaultError: ErrorMessage;

  constructor(messages: ErrorMessages = baseErrorMessages, defaultError: ErrorMessage = defaultErrorMessage) {
    this.messages = messages;
    this.defaultError = defaultError;
  }

  processError(error: unknown): ErrorMessage {
    if (isAxiosError(error)) {
      const status: number = error.response?.status ?? 500;
      const code: string | undefined = error.response?.data?.code;
      if (code && this.messages[code]) {
        const message: ErrorMessage | undefined = this.getMessageForCode(code, status);
        return message || this.defaultError;
      }
      const message: ErrorMessage | undefined = this.getMessageForStatus(status);
      return message || this.defaultError;
    }

    return this.defaultError;
  }

  processByStatusCode(status: number): ErrorMessage | undefined {
    return this.getMessageForStatus(status);
  }

  private getMessageForStatus(status: number): ErrorMessage | undefined {
    const messageConfig = this.messages[status];
    const severity = this.getSeverity(status);

    if (!messageConfig) {
      return;
    }
    return {
      ...messageConfig,
      status,
      severity,
    };
  }

  private getMessageForCode(code: string, status: number): ErrorMessage | undefined {
    const messageConfig = this.messages[code];
    const severity = this.getSeverity(status);

    if (!messageConfig) {
      return;
    }

    return {
      ...messageConfig,
      status,
      code,
      severity,
    };
  }

  private getSeverity(status: number): Required<ErrorMessage['severity']> {
    return status >= 400 && status <= 599 ? 'error' : 'warning';
  }

  updateMessages(messages: ErrorMessages): void {
    this.messages = { ...this.messages, ...messages };
  }

  updateDefaultError(defaultError: ErrorMessage): void {
    this.defaultError = defaultError;
  }
}
